import { forwardRef } from "react";
import type { ControllerProps, FieldError, FieldErrors, RegisterOptions } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";

import { useTranslations } from "@ui/providers/useTranslations";

import type { FieldWrapperProps } from "./FieldWrapper";
import { FieldWrapper } from "./FieldWrapper";

export type FormFieldProps = {
  id?: string;
  name: string;
  validation?: RegisterOptions;
  defaultValue?: string | boolean | number | { checked: boolean };
  render?: ControllerProps["render"];
  dataTestId?: string;
  hasEmptyError?: boolean;
  showCustomError?: boolean;
  labelClassName?: string;
} & Omit<FieldWrapperProps, "id" | "children">;

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      id,
      name,
      render,
      checkable,
      validation,
      error,
      defaultValue,
      dataTestId,
      hasEmptyError,
      showCustomError = false,
      labelClassName,
      ...fieldWrapperProps
    },
    ref
  ) => {
    const {
      formState: { errors },
      control,
      getValues,
    } = useFormContext();
    const { __ } = useTranslations();
    const formError = getError(errors, name);
    const fieldId = id || `${name}_control`;
    const errorMessage = formError?.message || error;
    const isRequired = Boolean(validation?.required);
    const isFilled = !!getValues(name);

    const getValidateRule = () => {
      if (validation?.validate) {
        return validation.validate;
      }

      if (isRequired) {
        const requiredErrorMessage =
          typeof validation?.required === "string" ? validation?.required : __("form_validation.field_required");

        return <T,>(value: T) => (typeof value === "string" ? !!value.trim() || requiredErrorMessage : true);
      }
    };

    if (!render) return null;

    return (
      <FieldWrapper
        {...fieldWrapperProps}
        id={fieldId}
        dataTestId={dataTestId}
        checkable={checkable}
        isFilled={isFilled}
        required={isRequired}
        error={errorMessage}
        ref={ref}
        hasEmptyError={hasEmptyError}
        showCustomError={showCustomError}
        labelClassName={labelClassName}
      >
        <Controller
          render={render}
          name={name}
          control={control}
          rules={{ ...validation, validate: getValidateRule() }}
          defaultValue={defaultValue}
        />
      </FieldWrapper>
    );
  }
);

const getError = (errors: FieldErrors, key: string): FieldError | undefined => {
  const keys = key.split(".");
  let error: FieldErrors | undefined = undefined;
  for (const k of keys) {
    if (!error) {
      error = errors;
    }
    error = error[k] as FieldErrors | undefined;
  }

  return error as FieldError | undefined;
};
